import React, { useCallback, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import { DEVICE_SIZE, mq } from '../../utils/mq';
import { useTranslation } from '../../utils/translation';
import { useLinkComponent } from '../../utils/component';

const EmbedAffilizzBox = styled.div`
  align-items: flex-start;
  box-shadow: 0px 0px 30px 0px var(--color-box-shadow);
  border-radius: 8px;
  padding: var(--spacer-fluid);
  display: flex;
  flex-direction: column;
  margin: var(--spacer-fluid) 0;
  gap: 4px;
  justify-content: flex-start;
  width: 100%;
`;

const AffilizzBoxImage = styled.img`
  margin: auto;
  width: 50%;
`;

const AffilizzBoxTitle = styled.div`
  font-weight: bold;
  font-family: 'Bitter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, serif;
  font-size: 20px;
`;

const AffilizzBoxNavTabs = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  flex-wrap: wrap;
  gap: 10px;
  margin: 10px 0;
  width: 100%;

  ${mq.lte(
    DEVICE_SIZE.LARGE,
    css`
      max-width: calc(100vw - 32px - 30px);
      overflow-x: auto;
    `,
  )}
`;

const AffilizzBoxNavActiveTab = styled.div`
  color: #ff0000;
  border: 1px solid #ff0000;
  padding: 0px 6px;
  border-radius: 4px;
  font-size: 15px;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  max-width: 100%;

  ${mq.lte(
    DEVICE_SIZE.LARGE,
    css`
      width: 100%;
    `,
  )}
`;

const AffilizzBoxNavInactiveTab = styled.div`
  cursor: pointer;
  color: #202b44;
  padding: 0px 6px;
  background-color: #edeef8;
  border: 1px solid #edeef8;
  border-radius: 4px;
  font-size: 15px;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  max-width: 100%;

  :hover {
    border-color: var(--theme-palette-grey-blue);
  }

  ${mq.lte(
    DEVICE_SIZE.LARGE,
    css`
      width: 100%;
    `,
  )}
`;

const AffilizzBoxPriceList = styled.div`
  display: flex;
  flex-direction: column;
`;

const AffilizzBoxPriceListUl = styled.ul``;
const AffilizzBoxPriceListLi = styled.li`
  display: grid;
  grid-template-columns: 16px 6fr 1fr 1fr;
  grid-gap: 10px;
  align-items: center;
  margin-bottom: 10px;

  &.firstPriceLi {
    font-weight: bolder;
  }
`;

const AffilizzBoxPriceListLiShopIcon = styled.img`
  grid-column: 1;
  width: 16px;
`;

const AffilizzBoxPriceListLiShopName = styled.div`
  grid-column: 2;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  white-space: nowrap;
  font-size: 18px;
`;

const AffilizzBoxPriceListLiShopNameMarketPlace = styled.div`
  font-size: 10px;
  line-height: 10px;
`;

const AffilizzBoxPriceListLiPrice = styled.div`
  grid-column: 3;
  white-space: nowrap;
  margin-right: 20px;
  padding-right: 20px;

  ${mq.lte(
    DEVICE_SIZE.LARGE,
    css`
      display: none;
    `,
  )}
`;

const AffilizzBoxPriceListLiCta = styled.div`
  border: solid 2px var(--theme-palette-red);
  grid-column: 4;
  white-space: nowrap;
  padding: 8px 10px;
  font-size: 18px;
  line-height: 1;
  width: initial;

  ${mq.lte(
    DEVICE_SIZE.LARGE,
    css`
      font-size: 16px;
    `,
  )}

  && {
    color: var(--theme-palette-white);
    text-decoration: none;
  }

  background: var(--theme-palette-red);
  border-radius: 6px;
  font-weight: bold;
  position: relative;
  text-align: center;
  transition: background-color 0.3s, border-color 0.3s;

  &:hover {
    background: var(--theme-palette-dark-red);
    border: solid 2px var(--theme-palette-dark-red);
  }
`;

const AffilizzBoxPriceListLiCtaMobile = styled.span`
  display: none;

  ${mq.lte(
    DEVICE_SIZE.LARGE,
    css`
      display: block;
    `,
  )}
`;

const AffilizzBoxPriceListLiCtaDesktop = styled.span`
  display: block;

  ${mq.lte(
    DEVICE_SIZE.LARGE,
    css`
      display: none;
    `,
  )}
`;

const SeeMoreAffilizzPrice = styled.div`
  width: 100%;
  text-align: center;
  font-weight: bold;
  text-decoration: underline;
  cursor: pointer;

  ${mq.lte(
    DEVICE_SIZE.LARGE,
    css`
      font-size: 16px;
    `,
  )}
`;

const getDisplayedPrice = (price: string): string => {
  const patternNoDec = /^\d+$/;
  const patternOneDec = /^\d+\.\d$/;

  let formattedPrice: string;
  switch (true) {
    case patternNoDec.test(price.toString()):
      formattedPrice = `${price}.00`;
      break;
    case patternOneDec.test(price.toString()):
      formattedPrice = `${price}0`;
      break;
    default:
      formattedPrice = price.toString();
  }

  const patternReplace = /\./;

  return `${formattedPrice.replace(patternReplace, ',')} €`;
};

interface AffilizzOfferProps {
  price?: string;
  offerId?: string;
  currency?: string;
  shopName?: string;
  shopIcon?: string;
  url?: string;
  stock?: boolean;
  marketplace?: boolean;
  condition?: string;
  promoCode?: string;
}

interface AffilizzTabProps {
  title?: string;
  productImage?: string;
  offers?: AffilizzOfferProps[];
}

interface AffilizzBoxProps {
  tabs: AffilizzTabProps[];
  eventCta: any;
  title?: string;
}

export const AffilizzBox = ({ ...props }: React.PropsWithChildren<AffilizzBoxProps>) => {
  const [seeMorePrice, setSeeMorePrice] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<number>(0);

  const { tabs, eventCta, title } = props;
  const t = useTranslation();
  const LinkComponent = useLinkComponent();

  const getMorePrice = useCallback(() => {
    setSeeMorePrice(!seeMorePrice);
  }, [setSeeMorePrice, seeMorePrice]);

  const changeActiveTab = useCallback(
    (tabIndex: number) => {
      setActiveTab(tabIndex);
    },
    [setActiveTab],
  );

  const currentTab: AffilizzTabProps = useMemo(() => {
    return tabs[activeTab];
  }, [tabs, activeTab]);

  return (
    <EmbedAffilizzBox>
      <AffilizzBoxImage src={currentTab.productImage} />
      <AffilizzBoxTitle>{title ?? currentTab.title}</AffilizzBoxTitle>
      <AffilizzBoxNavTabs>
        {tabs.length > 1 &&
          tabs.map((tab: AffilizzTabProps, index: number) => {
            const tabLabel = tab.title;

            if (activeTab === index) {
              return <AffilizzBoxNavActiveTab key={index}>{tabLabel}</AffilizzBoxNavActiveTab>;
            }

            return (
              <AffilizzBoxNavInactiveTab key={index} onClick={() => changeActiveTab(index)}>
                {tabLabel}
              </AffilizzBoxNavInactiveTab>
            );
          })}
      </AffilizzBoxNavTabs>
      <AffilizzBoxPriceList>
        <AffilizzBoxPriceListUl>
          {currentTab.offers?.map((offer: AffilizzOfferProps, index: number) => {
            const displayedPrice = getDisplayedPrice(offer.price);
            const { shopName } = offer;

            if (index < 5 || seeMorePrice === true) {
              return (
                <LinkComponent
                  onClick={() => eventCta(shopName, displayedPrice, 'affilizz', currentTab.title)}
                  href={offer.url}
                  key={offer.offerId}
                  openInNewWindow
                  rel={['nofollow']}
                >
                  <AffilizzBoxPriceListLi className={index ? null : 'firstPriceLi'}>
                    <AffilizzBoxPriceListLiShopIcon src={offer.shopIcon} />
                    <AffilizzBoxPriceListLiShopName>
                      {offer.shopName}
                      {offer.marketplace && (
                        <AffilizzBoxPriceListLiShopNameMarketPlace>
                          Marketplace
                        </AffilizzBoxPriceListLiShopNameMarketPlace>
                      )}
                    </AffilizzBoxPriceListLiShopName>
                    <AffilizzBoxPriceListLiPrice>{displayedPrice}</AffilizzBoxPriceListLiPrice>
                    <AffilizzBoxPriceListLiCta>
                      <AffilizzBoxPriceListLiCtaMobile>{displayedPrice}</AffilizzBoxPriceListLiCtaMobile>
                      <AffilizzBoxPriceListLiCtaDesktop>
                        {t('design.embed.affilizz.cta.see_offer')}
                      </AffilizzBoxPriceListLiCtaDesktop>
                    </AffilizzBoxPriceListLiCta>
                  </AffilizzBoxPriceListLi>
                </LinkComponent>
              );
            }

            return null;
          })}
          {currentTab.offers?.length > 5 && (
            <SeeMoreAffilizzPrice onClick={getMorePrice}>
              {seeMorePrice ? t('design.embed.affilizz.box.see_less') : t('design.embed.affilizz.box.see_more')}
            </SeeMoreAffilizzPrice>
          )}
        </AffilizzBoxPriceListUl>
      </AffilizzBoxPriceList>
    </EmbedAffilizzBox>
  );
};

export default AffilizzBox;
